import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import styles from './ProgramItem.module.scss';
import { Program } from '../../../types';
import { AusCourseContent, CourseContentType, useGetAusCourseContentsQuery } from '@api/graphql-codegen'
import UnitProgressModal from '@components/unit-progress-modal/UnitProgressModal';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import CoverContent from '@src/components/CoverContent/CoverContent';
import LessonProgressModal from '@src/components/lesson-progress-modal/LessonProgressModal';
import { getAusIconByTitle } from '@src/utils/helpers';
import Button from '@src/components/button/Button';
import { LordIcon } from '@src/components/lord-icon/lord-icon';
import { ausStudentVoiceSeriesScopeSequences, IconStandalone } from '@src/constants';
import IconArrowUp from 'src/assets/icons/dropdown-white.svg?react';
import PDFIcon from 'src/assets/icons/wired-outline-pdf.svg?react';
import PlusIcon from 'src/assets/icons/wired-outline-plus.svg?react';
import SkeletonCoverCard from '@src/components/SkeletonCoverCard/SkeletonCoverCard';

type Props = {
  program: Program;
  showEdit?: boolean;
  isParentApp?: boolean;
  selectedId?: string | null;
  selected?: boolean;
  handleSeleted?: (program: Program) => void;
  handleNoneSelect?: () => void;
  specialCourseId?: string;
  handleCardHovered?: (unitId: string, hovered: boolean) => void;
  handleSelectedCenterCard?: (centerCard: boolean) => void
  cardHovered?: boolean;
  hoveredUnitId?: string;
  refresh?: () => void;
  setLoadOthers: React.Dispatch<React.SetStateAction<number | null>>;
  loadOthers: number | null;
  index: number;
};

function Card(props: Props) {
  const {
    program,
    selectedId,
    selected,
    handleSeleted,
    isParentApp,
    loadOthers,
    setLoadOthers,
    index,
  } = props;

  const [showUnit, setShowUnit] = useState(false);
  const [showLesson, setShowLesson] = useState(false);
  const [showContentId, setShowContentId] = useState<string | undefined>(undefined);
  const { id } = program;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [shouldLoad, setShouldLoad] = useState(index === 0 ? true : false);
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);
  const [isReloading, setIsReloading] = useState<boolean>(
    sessionStorage.getItem('isReloading') === 'true'
  );

  useEffect(() => {
    const handlePageHide = () => {
      sessionStorage.setItem('isReloading', 'true');
    };
  
    window.addEventListener('pagehide', handlePageHide);
  
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []);

  useEffect(() => {
    if (isReloading) {
      setTimeout(() => {
        setIsReloading(false);
        sessionStorage.removeItem('isReloading');
      }, 1000); // Delay query execution by 1000ms
    }
  }, [isReloading]);

  const checkIfInView = useCallback(() => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const inView = rect.top >= 0 && rect.bottom <= window.innerHeight;

    if (inView) {
      setShouldLoad(true); // Load this grade
      setLoadOthers(index + 1); // Queue the next grade for loading
    }
  }, [setLoadOthers, index]);

  useEffect(() => {
    const handleScroll = () => {
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);

      scrollTimeout.current = setTimeout(() => {
        checkIfInView();
      }, 100); // Wait for 100ms after scroll stops
    };

    window.addEventListener('scroll', handleScroll);
    checkIfInView(); // Initial check
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    };
  }, [checkIfInView]);

  useEffect(() => {
    if (loadOthers === index) {
      const loadTimer = setTimeout(() => {
        setShouldLoad(true);
        setLoadOthers(index + 1); // Queue the next grade
      }, 100);

      return () => clearTimeout(loadTimer);
    }
  }, [loadOthers, index, setLoadOthers]);
  
  const {data: ausProgramData, loading} = useGetAusCourseContentsQuery(
    {variables: {input: {id}}, fetchPolicy: 'cache-first',
    skip: !shouldLoad || isReloading,
    }
  )

  const ausCourseContents = useMemo(() => [...ausProgramData?.getAUSCourseContents || []], [ausProgramData?.getAUSCourseContents])
  const isSelected = useMemo(() => {
    return (selected && selectedId === program.id) ? true : false;
  }, [selected, selectedId, program.id]);

  const handleOpenContent = useCallback((content: AusCourseContent) => {
    if (content.type === CourseContentType.UNIT) {
      setShowUnit(true);
      setShowLesson(false);
    } else if (content.type === CourseContentType.LESSON) {
      setShowLesson(true);
      setShowUnit(false);
    }
    setShowContentId(content.id);
  }, [])

  const handleCloseModal = useCallback(async() => {
    setShowUnit(false)
    setShowLesson(false)
    setShowContentId(undefined)
  }, []);

  const handleSelectProgram = useCallback(() => {
    handleSeleted?.(program)
  }, [handleSeleted, program]);

  const handleClickScopeLink = (programName: string) => {
    window.open(
      ausStudentVoiceSeriesScopeSequences[programName],
      "_blank"
    );
  }

  const Icon = getAusIconByTitle(program.name.replace(" Program", ""));

  return (
    <div className={styles.wrapper}>
      <div className={styles.backdrop} />
      <div className={styles.card} >
        <div ref={wrapperRef} className={styles.gradeTitleView}>
          <div className={styles.gradeGrayView}>
            <div className={styles.titleSection}>
              <Icon />
              <h1>{program.name.replace(" Program", "")}</h1>
            </div>
            <div className={styles.scopePdfView} onClick={() => handleClickScopeLink(program.name)}>
              <PDFIcon />
              <span>Scope & Sequence</span>
              <PlusIcon />
            </div>
          </div>
        </div>
        <div className={styles.topUnitView} style={{ maxHeight: isSelected ? '' : '408px' }} >
          <DndProvider backend={HTML5Backend}>
          <div className={styles.topUnitSubView}>
            {(loading || ausCourseContents.length === 0) ? <div className={styles.loadingWrapper}>
                <div className={styles.skeletonView} >
                  <SkeletonCoverCard />
                  <SkeletonCoverCard />
                  <SkeletonCoverCard />
                  <SkeletonCoverCard />
                </div>
              </div> : <div className={styles.collectionsView}>
              {ausCourseContents.length > 0 &&
              <div className={styles.content} >
                <div className={styles.unitView} >
                  <div className={styles.unitsDivWithFour} >
                  {ausCourseContents.map((content, index) => {
                    return (
                      <CoverContent
                        key={index}
                        type={"type"}
                        content={content}
                        handleOpenContent={handleOpenContent}
                      />
                    )
                  })}
                  </div>
                </div>
              </div>}
              {ausCourseContents.length > 4 ? <div className={!isSelected ? styles.gradientBackground : ''}></div> : null}

            </div>}
          </div>
          </DndProvider>
        </div>
        {ausCourseContents.length > 4 && <>
          {<div style={{ height: '30px' }}></div>}
          <div>
            <div className={styles.hoverTitleView}>
              <div className={styles.hoverTitleSubView}>
                <Button size='small' className={styles.extendViewButton} label={isSelected ? "View Less" : "View More"}
                  leftIcon={<LordIcon size={30} src={IconStandalone} isHover={true} colors={{ primary: '#FFFFFF', secondary: '#FFFFFF' }} target='.itemWrapper__bMNcg' />}
                  rightIcon={<IconArrowUp className={[styles.arrowIcon, isSelected ? styles.materialToggleClose : styles.materialToggleOpen].join(' ')} width={13} />} onClick={handleSelectProgram} />
              </div>
            </div>
          </div>
        </>}
      </div>
      {showContentId && showUnit ? <UnitProgressModal open={showUnit} onClose={handleCloseModal} unitId={showContentId} program={program} isDashboard isParentApp={isParentApp} /> : null}
      {showContentId && showLesson ? <LessonProgressModal open={showLesson} onClose={handleCloseModal} lessonId={showContentId} programName={program.name} /> : null}
    </div>
  );
}

export default Card;
